// CompilationForm.js
import React, { useState, useEffect } from "react"
import { Box, Typography } from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import CustomInputSelect from "../form-components/custom-input-select"
import CustomInputToggle from "../form-components/custom-input-toggle"
import CustomInputNumber from "../form-components/custom-input-number"
import CustomMultiFileInput from "../form-components/custom-multifile-input"
import CustomCheckbox from "../form-components/custom-checkbox"
import CustomButton from "../form-components/custom-button"
// @ts-ignore
import { useHistory } from "react-router-dom"
import useStore from "src/v2/utils/store"
import { useAlertStore } from "src/v2/utils/alert-store"
import consts from "src/v2/utils/consts.json"

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    backgroundImage: "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/background1.jpg)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    overflow: "hidden",
    paddingTop: theme.spacing(4),
    [theme.breakpoints.up("sm")]: {
      paddingTop: theme.spacing(6),
    },
    [theme.breakpoints.up("md")]: {
      paddingTop: theme.spacing(8),
    },
  },
  content: {
    position: "relative",
    zIndex: 2,
    color: "white",
    textAlign: "center",
    maxWidth: 1000,
    margin: "0 auto",
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.up("sm")]: {
      paddingBottom: theme.spacing(6),
    },
    [theme.breakpoints.up("md")]: {
      paddingBottom: theme.spacing(8),
    },
  },
  compilationFormContainer: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(4),
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  compilationFormContent: {
    width: "50%",
    [theme.breakpoints.only("xs")]: {
      width: "70%",
    },
    [theme.breakpoints.only("sm")]: {
      width: "80%",
    },
    maxWidth: 900,
  },
  buttonContainer: {
    marginTop: theme.spacing(4),
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center", // Center align the buttons
  },
}))

export default function CompilationForm() {
  const classes = useStyles()
  const history = useHistory()
  const [compilerVersion, setCompilerVersion] = useState("")
  const [compilerVersionHasError, setCompilerVersionHasError] = useState(false)
  const [compilerVersionErrorMessage, setCompilerVersionErrorMessage] =
    useState("")
  const [mainContract, setMainContract] = useState("")
  const [mainContractHasError, setMainContractHasError] = useState(false)
  const [mainContractErrorMessage, setMainContractMessage] = useState("")
  const [useOptimizationFlags, setUseOptimizationFlagsValue] = useState(true)
  const [optimizationRuns, setoptimizationRuns] = useState(200)
  const [optimizationRunsHasError, setOptimizationRunsHasError] =
    useState(false)
  const [optimizationRunsErrorMessage, setOptimizationRunsErrorMessage] =
    useState("")
  const [viaIR, setViaIR] = useState(false)
  const [includeSimulationTests, setIncludeSimulationTests] = useState(true)
  const [architectureDocuments, setArchitectureDocuments] = useState([])
  const [specificationsDocuments, setSpecificationsDocuments] = useState([])
  const [submitTestDetailsLoading, setSubmitTestDetailsLoading] =
    useState(false)
  const setCompiling = useStore((state) => state.setCompiling)
  const fetchSolidityCompilers = useStore(
    (state) => state.fetchSolidityCompilers,
  )
  const solidityCompilers = useStore((state) => state.solidityCompilers)
  const versionInfo = useStore((state) => state.versionInfo)
  const showAlert = useAlertStore((state) => state.showAlert)
  const mainContractStore = useStore((state) => state.mainContract)
  const mainFile = useStore((state) => state.mainFile)
  const allContracts = useStore((state) => state.allContracts)
  const allSolFiles = useStore((state) => state.uploadedSolFiles)
  const setCompilationPayload = useStore((state) => state.setCompilationPayload)

  // Convert solidityCompilers to options for CustomInputSelect
  const compilerOptions = solidityCompilers.map((compiler) => {
    return { value: compiler, label: compiler.replace(/^v/, "").split("+")[0] }
  })

  // Build contract options for select
  const contractOptions = (allContracts || []).map((contract) => ({
    value: contract,
    label: contract,
  }))

  useEffect(() => {
    fetchSolidityCompilers(showAlert)
  }, [fetchSolidityCompilers, showAlert])

  useEffect(() => {
    // Set default to maxUsableVersion if available
    if (versionInfo && versionInfo.maxUsableVersion) {
      setCompilerVersion(versionInfo.maxUsableVersion)
    }
  }, [versionInfo])

  // Set default main contract from store
  useEffect(() => {
    if (mainContractStore && contractOptions.length > 0) {
      setMainContract(mainContractStore)
    }
  }, [mainContractStore, contractOptions])

  const handleSelectCompilerChange = (value) => {
    setCompilerVersion(value)
    if (value === "") {
      setCompilerVersionHasError(true)
      setCompilerVersionErrorMessage("Please select an option.")
    } else {
      setCompilerVersionHasError(false)
      setCompilerVersionErrorMessage("")
    }
  }

  const handleSelectContractChange = (value) => {
    setMainContract(value)
    if (value === "") {
      setMainContractHasError(true)
      setMainContractMessage("Please select a contract.")
    } else {
      setMainContractHasError(false)
      setMainContractMessage("")
    }
  }

  // Validation and submit handler
  const onTestSubmit = async () => {
    setSubmitTestDetailsLoading(true)
    let valid = true

    if (!compilerVersion) {
      setCompilerVersionHasError(true)
      setCompilerVersionErrorMessage("Please select a compiler version.")
      valid = false
    }
    if (!mainContract) {
      setMainContractHasError(true)
      setMainContractMessage("Please select a contract.")
      valid = false
    }
    if (!optimizationRuns || isNaN(optimizationRuns) || optimizationRuns <= 0) {
      setOptimizationRunsHasError(true)
      setOptimizationRunsErrorMessage("Enter a valid number of runs.")
      valid = false
    }

    if (!valid) {
      setSubmitTestDetailsLoading(false)
      showAlert({
        message: "Please fix the errors in the form.",
        severity: "error",
        title: "Validation Error",
      })
      return
    }

    // Build compilerSettings object as per requirements
    let compilerSettings = {}
    if (useOptimizationFlags) {
      compilerSettings.optimizer = { enabled: true, runs: optimizationRuns }
    }
    if (viaIR) {
      compilerSettings.viaIR = true
    }
    if ((!useOptimizationFlags || !compilerSettings.optimizer) && !viaIR) {
      // @ts-ignore
      compilerSettings = {}
    }

    // Helper function to read file contents as base64.
    // We send raw file bytes (base64-encoded so they survive JSON transport)
    // to the backend, which uses officeparser to extract text from PDF / DOCX / etc.
    const readFileAsBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          // readAsDataURL returns a string like "data:<mime>;base64,<payload>".
          // Strip the prefix so the backend receives pure base64.
          const result = e.target.result || ""
          const commaIdx = typeof result === "string" ? result.indexOf(",") : -1
          resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result)
        }
        reader.onerror = (e) => reject(e)
        reader.readAsDataURL(file)
      })
    }

    try {
      // Process architecture documents
      const processedArchitectureDocuments = await Promise.all(
        architectureDocuments.map(async (file) => ({
          name: file.name,
          mimetype: file.type || undefined,
          content: await readFileAsBase64(file)
        }))
      )

      // Process specifications documents  
      const processedSpecificationsDocuments = await Promise.all(
        specificationsDocuments.map(async (file) => ({
          name: file.name,
          mimetype: file.type || undefined,
          content: await readFileAsBase64(file)
        }))
      )

      // Prepare payload for compilation API
      const getCompiledPayload = {
        compilerSettings,
        compilerVersion,
        files: allSolFiles,
        mainContract,
        mainFile,
      }

      // Prepare formData for submitTestDetails
      const filesArray = (allSolFiles || []).map((file) => ({
        name: file.path,
        content: file.content,
      }))
      const submitTestDetailsPayload = {
        compilerVersion,
        mainContract,
        mainFile,
        compilerSettings,
        files: filesArray,
        architectureDocuments: processedArchitectureDocuments,
        specificationsDocuments: processedSpecificationsDocuments,
        includeSimulationTests,
      }

      // Save both payloads in store for later use
      setCompilationPayload({
        getCompiledPayload,
        submitTestDetailsPayload,
      })
      setSubmitTestDetailsLoading(false)
      setCompiling(true)
    } catch (error) {
      setSubmitTestDetailsLoading(false)
      showAlert({
        message: `Error reading uploaded files: ${error.message}`,
        severity: "error",
        title: "File Reading Error",
      })
    }
  }

  return (
    <Box>
      {/* -----------------------------banner section----------------------------- */}
      <Box className={classes.root}>
        <Box className={classes.content}>
          <Typography variant="h3" gutterBottom style={{ fontWeight: "bold" }}>
            One last step before the AI agent generates your report with
            findings and recommendations.
          </Typography>
        </Box>
      </Box>

      {/* -----------------------------compilation form section----------------------------- */}
      <Box mt={4} className={classes.compilationFormContainer}>
        <Box className={classes.compilationFormContent}>
          <CustomInputSelect
            label={"Choose Solidity Compiler"}
            required={true}
            value={compilerVersion}
            onChange={handleSelectCompilerChange}
            options={compilerOptions}
            hasError={compilerVersionHasError}
            errorMessage={compilerVersionErrorMessage}
          />

          <CustomInputSelect
            label={"Choose Main Contract"}
            required={true}
            value={mainContract}
            onChange={handleSelectContractChange}
            options={contractOptions}
            hasError={mainContractHasError}
            errorMessage={mainContractErrorMessage}
          />

          <CustomInputToggle
            label={"Use optimization flags?"}
            required={true}
            value={useOptimizationFlags}
            onChange={setUseOptimizationFlagsValue}
          />

          <CustomInputNumber
            label="Optimization runs"
            required={true}
            value={optimizationRuns}
            onChange={setoptimizationRuns}
            hasError={optimizationRunsHasError}
            errorMessage={optimizationRunsErrorMessage}
          />

          <CustomInputToggle
            label={"Via IR?"}
            required={true}
            value={viaIR}
            onChange={setViaIR}
          />

          <Box mt={2} mb={1} display="flex" justifyContent="flex-start">
            <CustomCheckbox
              label="Run AI-generated simulation tests"
              checked={includeSimulationTests}
              onChange={setIncludeSimulationTests}
            />
          </Box>

          <CustomMultiFileInput
            id="specifications-documents-input"
            label="Requirements Documents"
            description="Functional requirements / specification documents that describe what the contract should do."
            acceptedFileTypes={[
              ".pdf",
              ".txt",
              ".docx"
            ]}
            files={specificationsDocuments}
            onFilesChange={setSpecificationsDocuments}
            maxFileSizeMB={10}
            maxTotalSizeMB={30}
            maxFiles={5}
            onValidationError={(message) =>
              showAlert({ message, severity: "warning", title: "Upload limit" })
            }
          />

          <CustomMultiFileInput
            label="Architecture Documents"
            description="Technical architecture documents (components, data model, threats, risks, deployment, etc.)."
            acceptedFileTypes={[
              ".pdf"
            ]}
            files={architectureDocuments}
            onFilesChange={setArchitectureDocuments}
            maxFileSizeMB={10}
            maxTotalSizeMB={30}
            maxFiles={5}
            onValidationError={(message) =>
              showAlert({ message, severity: "warning", title: "Upload limit" })
            }
          />

          <Box className={classes.buttonContainer}>
            <CustomButton
              id={"start-testing-button"}
              onClick={onTestSubmit}
              style={{ width: "auto" }}
              loading={submitTestDetailsLoading}
            >
              Start Testing
            </CustomButton>
            <CustomButton
              onClick={() => history.push(consts.ROUTES.LANDING)}
              style={{ width: "auto" }}
              variant="link"
            >
              Back
            </CustomButton>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
